/**
 * Ad slot configuration and A/B variant selection.
 *
 * Each slot maps to a set of weighted variants, mirroring the legacy
 * `app.ad_slots` parameters, e.g.:
 *
 *   car_rental:
 *     variants:
 *       discovercars: 50
 *       travelpayouts_rentalcars: 50
 *
 * The chosen variant for a slot is remembered per visitor in localStorage,
 * but only for one minute. After the minute expires the variant is picked
 * again (weighted) and remembered for another minute.
 */

export type VariantWeights = Record<string, number>;

export interface AdSlotConfig {
  variants: VariantWeights;
}

export interface AdSlotsConfig {
  [slot: string]: AdSlotConfig;
}

/** Context passed to ad variants, e.g. the castle's location on the details page. */
export interface AdSlotContext {
  lat?: number | null;
  lng?: number | null;
  address?: string | null;
}

export const AD_SLOTS: AdSlotsConfig = {
  car_rental: {
    variants: {
      discovercars: 50,
      travelpayouts_rentalcars: 50,
    },
  },
  accommodation: {
    variants: {
      stay22: 50,
      travelpayouts_hotels: 50,
    },
  },
};

/**
 * Settings shared by the ad provider widgets. Hardcoded one-off values.
 */
export const AD_SETTINGS = {
  locale: 'en',
  currency: 'eur',
  trs: '494555',
  shmarker: '700689.cas',
  discovercarsUrl: 'https://www.discovercars.com/?a_aid=codesensus&a_bid=f29909e9&chan=cas',
  stay22Aid: 'arch',
  stay22Campaign: 'cas',
};

const VARIANT_STORAGE_PREFIX = 'ad_slot_';
const VARIANT_TTL_MS = 60_000;

/** Picks a variant honouring the configured weights (e.g. 50/50). */
export function pickVariant(variants: VariantWeights): string | null {
  const entries = Object.entries(variants).filter(([, weight]) => weight > 0);
  if (entries.length === 0) {
    return null;
  }

  const total = entries.reduce((sum, [, weight]) => sum + weight, 0);
  let random = Math.random() * total;

  for (const [variant, weight] of entries) {
    random -= weight;
    if (random < 0) {
      return variant;
    }
  }

  return entries[entries.length - 1][0];
}

/** Reads a previously assigned variant for a slot if it has not expired. */
export function getStoredVariant(slot: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(`${VARIANT_STORAGE_PREFIX}${slot}`);
    if (!raw) {
      return null;
    }
    const data = JSON.parse(raw);
    if (!data || typeof data.variant !== 'string' || typeof data.expiresAt !== 'number') {
      return null;
    }
    return data.expiresAt > Date.now() ? data.variant : null;
  } catch {
    return null;
  }
}

/** Persists the chosen variant with a one-minute expiry. */
export function storeVariant(slot: string, variant: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const payload = JSON.stringify({ variant, expiresAt: Date.now() + VARIANT_TTL_MS });
    window.localStorage.setItem(`${VARIANT_STORAGE_PREFIX}${slot}`, payload);
  } catch {
    // Ignore storage errors (private mode, disabled storage, etc.)
  }
}

/** Resolves the variant for a slot, remembering the first pick per visitor. */
export function resolveVariant(slot: string): string | null {
  const config = AD_SLOTS[slot];
  if (!config || typeof config.variants !== 'object') {
    return null;
  }

  const stored = getStoredVariant(slot);
  if (stored && stored in config.variants) {
    return stored;
  }

  const variant = pickVariant(config.variants);
  if (variant) {
    storeVariant(slot, variant);
  }
  return variant;
}
